import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { AnimatePresence, useReducedMotion, useScroll } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
//
import { Product } from '../../data/products';
import { PATH_PAGE } from '../../routes/paths';
import { CONTAINER, STAGE } from '../../theme/stage';
import { MENU, WORDMARK } from './copy';
import { focusTarget } from './focus';
import MenuButton from './menu/MenuButton';
import MenuPanel from './menu/MenuPanel';
import useActiveSection from './menu/useActiveSection';
import useMenuDialog from './menu/useMenuDialog';
import Wordmark from './Wordmark';

// ----------------------------------------------------------------------

type Props = {
  products: Product[];
};

const FROST_AFTER = 24;

export default function SiteHeader({ products }: Props) {
  const { pathname, key } = useLocation();

  const [open, setOpen] = useState(false);

  const headerRef = useRef<HTMLElement>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const panelRef = useRef<HTMLDivElement>(null);

  const active = useActiveSection(pathname === PATH_PAGE.home);

  const { scrollY } = useScroll();

  const reduced = useReducedMotion();

  const close = useCallback(() => setOpen(false), []);

  const follow = useCallback(
    (href: string, event?: MouseEvent) => {
      setOpen(false);
      const [path, id] = href.split('#');
      const target = id && path === pathname ? document.getElementById(id) : null;
      if (target) {
        event?.preventDefault();
        target.scrollIntoView();
        requestAnimationFrame(() => focusTarget(target));
      }
      if (href === pathname) {
        requestAnimationFrame(() =>
          window.scrollTo({ top: 0, behavior: open || reduced ? 'auto' : 'smooth' })
        );
      }
    },
    [pathname, open, reduced]
  );

  useMenuDialog({ open, onClose: close, header: headerRef, trigger: buttonRef, dialog: panelRef });

  useEffect(() => setOpen(false), [key]);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) {
      return undefined;
    }
    const update = (y: number) => {
      header.dataset.scrolled = String(y > FROST_AFTER);
    };
    update(scrollY.get());
    return scrollY.onChange(update);
  }, [scrollY]);

  return (
    <Box
      ref={headerRef}
      component="header"
      data-chrome
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 45,
        height: { xs: 56, sm: 64 },
        mb: { xs: '-56px', sm: '-64px' },
        transition: 'background-color 200ms linear',
        '&[data-scrolled="true"]': { bgcolor: STAGE.ground },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          borderBottom: `1px solid ${STAGE.line}`,
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 200ms linear',
          '[data-scrolled="true"] > &': { opacity: 1 },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      />

      <Box
        sx={{
          ...CONTAINER,
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '100%',
          '@supports (-webkit-touch-callout: none)': {
            '@media (max-width: 599.95px)': { pb: '12px' },
          },
        }}
      >
        <Box
          component={RouterLink}
          to={PATH_PAGE.home}
          aria-label={WORDMARK.homeLabel}
          onClick={() => follow(PATH_PAGE.home)}
          sx={{ display: 'inline-flex', alignItems: 'center', height: 44, textDecoration: 'none' }}
        >
          <Wordmark />
        </Box>

        <MenuButton ref={buttonRef} open={open} onClick={() => setOpen((value) => !value)} />
      </Box>

      <AnimatePresence>
        {open && (
          <MenuPanel
            key={MENU.id}
            ref={panelRef}
            products={products}
            pathname={pathname}
            active={active}
            onNavigate={follow}
          />
        )}
      </AnimatePresence>
    </Box>
  );
}
